import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { TextArea } from '@shared/components/ui/textarea';
import { forwardRef, useImperativeHandle } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { FulfillmentData, RateItem, RateItemFormRef } from '../../models/rate.types';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';

const fulfillmentFormSchema = z.object({
  notes: z.string().optional(),
  packing_fixed_fee: z.string().min(1, 'validation.packingFeeRequired'),
  packing_extra_lines_fee: z.string().optional(),
  packing_since_line: z.string().optional(),
  packing_attachment_fee: z.string().optional(),
  reused_fixed_fee: z.string().min(1, 'validation.refusedFeeRequired'),
  reused_extra_lines_fee: z.string().optional(),
  reused_since_line: z.string().optional(),
});

type FulfillmentFormData = z.infer<typeof fulfillmentFormSchema>;

interface AddFulfillmentItemFormProps {
  initialData?: RateItem;
}

export const AddFulfillmentItemForm = forwardRef<RateItemFormRef, AddFulfillmentItemFormProps>(
  ({ initialData }, ref) => {
    const { t } = useTranslation('ratesModule');
    const currency = useCurrency();

    const fulfillmentData = initialData?.data as FulfillmentData | undefined;

    const {
      control,
      trigger,
      getValues,
      formState: { errors },
    } = useForm<FulfillmentFormData>({
      resolver: zodResolver(fulfillmentFormSchema),
      mode: 'onChange',
      defaultValues: {
        notes: initialData?.notes || '',
        packing_fixed_fee:
          fulfillmentData?.packing_fee?.fixed_fee != null ? String(fulfillmentData.packing_fee.fixed_fee) : '',
        packing_extra_lines_fee:
          fulfillmentData?.packing_fee?.extra_lines_fee != null
            ? String(fulfillmentData.packing_fee.extra_lines_fee)
            : '',
        packing_since_line:
          fulfillmentData?.packing_fee?.extra_lines_fee_since_line_number != null
            ? String(fulfillmentData.packing_fee.extra_lines_fee_since_line_number)
            : '1',
        packing_attachment_fee:
          fulfillmentData?.packing_fee?.attachment_fee != null
            ? String(fulfillmentData.packing_fee.attachment_fee)
            : '',
        reused_fixed_fee:
          fulfillmentData?.reused_fee?.fixed_fee != null ? String(fulfillmentData.reused_fee.fixed_fee) : '',
        reused_extra_lines_fee:
          fulfillmentData?.reused_fee?.extra_lines_fee != null
            ? String(fulfillmentData.reused_fee.extra_lines_fee)
            : '',
        reused_since_line:
          fulfillmentData?.reused_fee?.extra_lines_fee_since_line_number != null
            ? String(fulfillmentData.reused_fee.extra_lines_fee_since_line_number)
            : '1',
      },
    });

    useImperativeHandle(
      ref,
      () => ({
        getData: async () => {
          const isValid = await trigger();
          if (!isValid) return null;

          const values = getValues();

          // BOTH packing_fee and reused_fee are REQUIRED (backend validation)
          const hasPackingFee = values.packing_fixed_fee;
          const hasReusedFee = values.reused_fixed_fee;

          if (!hasPackingFee || !hasReusedFee) return null;

          const packingFee = {
            fixed_fee: parseFloat(values.packing_fixed_fee || '0'),
            extra_lines_fee: parseFloat(values.packing_extra_lines_fee || '0'),
            extra_lines_fee_since_line_number: parseInt(values.packing_since_line || '1'),
            ...(values.packing_attachment_fee?.trim()
              ? { attachment_fee: parseFloat(values.packing_attachment_fee) }
              : {}),
          };

          const data = {
            packing_fee: packingFee,
            reused_fee: {
              fixed_fee: parseFloat(values.reused_fixed_fee || '0'),
              extra_lines_fee: parseFloat(values.reused_extra_lines_fee || '0'),
              extra_lines_fee_since_line_number: parseInt(values.reused_since_line || '1'),
            },
          };

          return {
            title: t('itemTypes.fulfillment'),
            type: 'fulfillment' as const,
            notes: values.notes || null,
            data,
          };
        },
      }),
      [trigger, getValues, t],
    );

    return (
      <div className="space-y-4">
        <div className="border border-card-border rounded-lg p-3">
          <p className="font-medium text-sm mb-3">{t('form.fulfillment.packingFee')} *</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div>
              <Label className="text-xs">{t('form.fulfillment.fixedFee', { currency })} *</Label>
              <Controller
                name="packing_fixed_fee"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    className={errors.packing_fixed_fee ? 'border-error' : ''}
                  />
                )}
              />
              {errors.packing_fixed_fee && (
                <p className="text-xs text-error mt-1">{t(errors.packing_fixed_fee.message!)}</p>
              )}
            </div>
            <div>
              <Label className="text-xs">{t('form.fulfillment.extraLinesFee', { currency })}</Label>
              <Controller
                name="packing_extra_lines_fee"
                control={control}
                render={({ field }) => <Input {...field} type="number" step="0.01" placeholder="0.00" />}
              />
            </div>
            <div>
              <Label className="text-xs">{t('form.fulfillment.sinceLineNumber')}</Label>
              <Controller
                name="packing_since_line"
                control={control}
                render={({ field }) => <Input {...field} type="number" min="1" placeholder="1" />}
              />
            </div>
            <div>
              <Label className="text-xs">{t('form.fulfillment.attachmentFee', { currency })}</Label>
              <Controller
                name="packing_attachment_fee"
                control={control}
                render={({ field }) => <Input {...field} type="number" step="0.01" placeholder="0.00" />}
              />
            </div>
          </div>
        </div>

        <div className="border border-card-border rounded-lg p-3">
          <p className="font-medium text-sm mb-3">{t('form.fulfillment.refusedFee')} *</p>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">{t('form.fulfillment.fixedFee', { currency })} *</Label>
              <Controller
                name="reused_fixed_fee"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    className={errors.reused_fixed_fee ? 'border-error' : ''}
                  />
                )}
              />
              {errors.reused_fixed_fee && (
                <p className="text-xs text-error mt-1">{t(errors.reused_fixed_fee.message!)}</p>
              )}
            </div>
            <div>
              <Label className="text-xs">{t('form.fulfillment.extraLinesFee', { currency })}</Label>
              <Controller
                name="reused_extra_lines_fee"
                control={control}
                render={({ field }) => <Input {...field} type="number" step="0.01" placeholder="0.00" />}
              />
            </div>
            <div>
              <Label className="text-xs">{t('form.fulfillment.sinceLineNumber')}</Label>
              <Controller
                name="reused_since_line"
                control={control}
                render={({ field }) => <Input {...field} type="number" min="1" placeholder="1" />}
              />
            </div>
          </div>
        </div>

        <div>
          <Label>{t('form.notes')}</Label>
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <TextArea {...field} value={field.value || ''} placeholder={t('form.notesPlaceholder')} rows={2} />
            )}
          />
        </div>
      </div>
    );
  },
);

AddFulfillmentItemForm.displayName = 'AddFulfillmentItemForm';
